// src/pages/admin/AdminNoticePage.jsx
import { useEffect, useState } from "react";
import styles from "./AdminSupport.module.css";

const PAGE_SIZE = 10;

export default function AdminNoticePage() {
  const [data, setData] = useState({
    content: [],
    totalElements: 0,
    totalPages: 0,
    number: 0,
  });
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [pinned, setPinned] = useState(false);

  const resetForm = () => {
    setEditingId(null);
    setTitle("");
    setContent("");
    setPinned(false);
  };

  const loadList = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/support/notices?page=${page}&size=${PAGE_SIZE}`, {
        credentials: "include",
      });

      if (!res.ok) {
        console.error("공지 목록 응답 에러:", res.status, res.statusText);
        throw new Error("공지 목록 조회 실패");
      }

      const json = await res.json();
      let pageData;

      if (Array.isArray(json)) {
        // 1) 배열 형태 응답
        pageData = {
          content: json,
          totalElements: json.length,
          totalPages: 1,
          number: 0,
        };
      } else if (Array.isArray(json.content)) {
        // 2) Page<T> 형태
        pageData = json;
      } else if (Array.isArray(json.items)) {
        // 3) { items: [...], total: n } 형태
        const total = json.total ?? json.items.length;
        pageData = {
          content: json.items,
          totalElements: total,
          totalPages: Math.ceil(total / PAGE_SIZE),
          number: page,
        };
      } else {
        console.warn("알 수 없는 공지 목록 응답 구조:", json);
        pageData = {
          content: [],
          totalElements: 0,
          totalPages: 0,
          number: 0,
        };
      }

      setData(pageData);
    } catch (e) {
      console.error(e);
      alert("공지 목록을 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleEditClick = (notice) => {
    setEditingId(notice.id);
    setTitle(notice.title);
    setContent(notice.content || "");
    setPinned(notice.pinned);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("이 공지를 삭제하시겠습니까?")) return;
    const res = await fetch(`/api/support/notices/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (res.ok) {
      if (editingId === id) resetForm();
      loadList();
    } else {
      alert("삭제에 실패했습니다.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const body = { title, content, pinned };

    const url = editingId
      ? `/api/support/notices/${editingId}`
      : "/api/support/notices";
    const method = editingId ? "PUT" : "POST";

    const res = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });

    if (res.ok) {
      resetForm();
      loadList();
    } else {
      alert("저장에 실패했습니다.");
    }
  };

  return (
    <main className={styles.page}>
      <h1 className={styles.pageTitle}>공지사항 관리</h1>

      <div className={styles.split}>
        {/* 리스트 영역 */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2>공지 목록</h2>
          </div>

          {loading ? (
            <p>로딩 중...</p>
          ) : (
            <>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th style={{ width: "60px" }}>ID</th>
                    <th style={{ textAlign: "left"}}>제목</th>
                    <th style={{ width: "80px" }}>고정</th>
                    <th style={{ width: "160px" }}>작성일</th>
                    <th className={styles.centerCol} style={{ width: "120px" }}>
                      관리
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.content.map((n) => (
                    <tr
                      key={n.id}
                      onClick={() => handleEditClick(n)} // 행 클릭 → 수정 모드
                    >
                      <td>{n.id}</td>
                      <td className={n.pinned ? styles.pinned : ""} style={{ textAlign: "left"}}>{n.title}</td>
                      <td>{n.pinned ? "Y" : ""}</td>
                      <td>{n.createdAt?.slice(0, 16).replace("T", " ")}</td>
                      <td className={`${styles.centerCol} ${styles.actionsCell}`}>
                        <button
                          type="button"
                          className={styles.smallBtnDanger}
                          onClick={(e) => {
                            e.stopPropagation(); // 행 클릭(수정) 방지
                            handleDelete(n.id);
                          }}
                        >
                          삭제
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className={styles.pagination}>
                <button
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                >
                  이전
                </button>
                <span>
                  {page + 1} / {data.totalPages || 1}
                </span>
                <button
                  disabled={page + 1 >= data.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  다음
                </button>
              </div>
            </>
          )}
        </section>

        {/* 작성/수정 폼 영역 */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2>{editingId ? "공지 수정" : "공지 등록"}</h2>
            {editingId && (
              <button
                type="button"
                className={styles.smallBtn}
                onClick={resetForm}
              >
                새 공지 작성
              </button>
            )}
          </div>

          <form className={styles.form} onSubmit={handleSubmit}>
            <label>
              제목
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </label>

            <label>
              내용
              <textarea
                rows={10}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                required
              />
            </label>

            <label className={styles.inline}>
              <input
                type="checkbox"
                checked={pinned}
                onChange={(e) => setPinned(e.target.checked)}
              />
              상단 고정
            </label>

            <div className={styles.actions}>
              <button type="submit">
                {editingId ? "수정 저장" : "등록"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </main>
  );
}
